import { supabase, ActividadEconomica } from '../lib/supabase';
import { getLumisialesFiltrados, extraerDatosJerarquia, FiltrosAdminBase } from './adminServiceHelpers';

export interface FiltrosAdminActividades extends FiltrosAdminBase {
  estado?: 'planificacion' | 'en_curso' | 'finalizada' | 'archivada';
  busqueda?: string;
}

export interface LumisialActividadesGroup {
  lumisialId: string;
  lumisialNombre: string;
  lumisialCiudad: string;
  totalActividades: number;
  totalIngresos: number;
  totalCostos: number;
  utilidadNeta: number;
  actividades: ActividadEconomica[];
}

export interface DiocesisActividadesGroup {
  diocesisId: string;
  diocesisNombre: string;
  diocesisTipo: 'diocesis' | 'prefectura';
  totalActividades: number;
  totalIngresos: number;
  totalCostos: number;
  utilidadNeta: number;
  lumisiales: LumisialActividadesGroup[];
}

export interface ProvinciaActividadesGroup {
  provinciaId: string;
  provinciaNombre: string;
  totalActividades: number;
  totalIngresos: number;
  totalCostos: number;
  utilidadNeta: number;
  diocesis: DiocesisActividadesGroup[];
}

export interface HierarchicalActividadesStats {
  provincias: ProvinciaActividadesGroup[];
}

export interface DiocesisJADGroup {
  diocesisId: string;
  diocesisNombre: string;
  diocesisTipo: 'diocesis' | 'prefectura';
  totalActividades: number;
  totalIngresos: number;
  totalCostos: number;
  utilidadNeta: number;
  actividades: ActividadEconomica[];
}

export interface ProvinciaJANGroup {
  provinciaId: string;
  provinciaNombre: string;
  totalActividades: number;
  totalIngresos: number;
  totalCostos: number;
  utilidadNeta: number;
  actividades: ActividadEconomica[];
}

class ActividadAdminService {
  async getActividadesGrouped(filtros: FiltrosAdminActividades): Promise<HierarchicalActividadesStats> {
    try {
      const lumisiales = await getLumisialesFiltrados(filtros);
      if (!lumisiales || lumisiales.length === 0) return { provincias: [] };

      const lumisialIds = lumisiales.map(l => l.id);

      let actividadesQuery = supabase
        .from('actividades_economicas')
        .select('*')
        .in('lumisial_id', lumisialIds)
        .order('fecha_inicio', { ascending: false });

      if (filtros.estado) {
        actividadesQuery = actividadesQuery.eq('estado', filtros.estado);
      } else {
        actividadesQuery = actividadesQuery.neq('estado', 'archivada');
      }

      if (filtros.busqueda) {
        actividadesQuery = actividadesQuery.ilike('nombre', `%${filtros.busqueda}%`);
      }

      const { data: actividades, error: actividadesError } = await actividadesQuery;
      if (actividadesError) throw actividadesError;

      const actividadesPorLumisial = new Map<string, ActividadEconomica[]>();
      for (const act of (actividades || [])) {
        const grupo = actividadesPorLumisial.get(act.lumisial_id) || [];
        grupo.push(act);
        actividadesPorLumisial.set(act.lumisial_id, grupo);
      }

      const provinciasMap = new Map<string, ProvinciaActividadesGroup>();

      for (const lumisial of lumisiales) {
        const acts = actividadesPorLumisial.get(lumisial.id) || [];

        const totalIngresos = acts.reduce((s, a) => s + Number(a.total_ingresos || 0), 0);
        const totalCostos = acts.reduce((s, a) => s + Number(a.total_costos || 0), 0);

        const lumisialGroup: LumisialActividadesGroup = {
          lumisialId: lumisial.id,
          lumisialNombre: lumisial.nombre,
          lumisialCiudad: lumisial.ciudad || '',
          totalActividades: acts.length,
          totalIngresos,
          totalCostos,
          utilidadNeta: totalIngresos - totalCostos,
          actividades: acts,
        };

        const { provinciaId, provinciaNombre, diocesisId, diocesisNombre, diocesisTipo } = extraerDatosJerarquia(lumisial);

        if (!provinciasMap.has(provinciaId)) {
          provinciasMap.set(provinciaId, {
            provinciaId,
            provinciaNombre,
            totalActividades: 0,
            totalIngresos: 0,
            totalCostos: 0,
            utilidadNeta: 0,
            diocesis: [],
          });
        }

        const provincia = provinciasMap.get(provinciaId)!;
        let diocesisGroup = provincia.diocesis.find(d => d.diocesisId === diocesisId);

        if (!diocesisGroup) {
          diocesisGroup = {
            diocesisId,
            diocesisNombre,
            diocesisTipo,
            totalActividades: 0,
            totalIngresos: 0,
            totalCostos: 0,
            utilidadNeta: 0,
            lumisiales: [],
          };
          provincia.diocesis.push(diocesisGroup);
        }

        diocesisGroup.lumisiales.push(lumisialGroup);
        diocesisGroup.totalActividades += lumisialGroup.totalActividades;
        diocesisGroup.totalIngresos += lumisialGroup.totalIngresos;
        diocesisGroup.totalCostos += lumisialGroup.totalCostos;
        diocesisGroup.utilidadNeta += lumisialGroup.utilidadNeta;

        provincia.totalActividades += lumisialGroup.totalActividades;
        provincia.totalIngresos += lumisialGroup.totalIngresos;
        provincia.totalCostos += lumisialGroup.totalCostos;
        provincia.utilidadNeta += lumisialGroup.utilidadNeta;
      }

      return { provincias: Array.from(provinciasMap.values()) };
    } catch (error) {
      console.error('Error al obtener actividades agrupadas:', error);
      throw error;
    }
  }

  async getActividadesJADGrouped(filtros?: { provinciaEclesiasticaId?: string }): Promise<DiocesisJADGroup[]> {
    try {
      // Las actividades JAD tienen provincia_eclesiastica_id=NULL (owner es la diócesis).
      // Para filtrar por provincia hay que resolver los diocesis_id de esa provincia primero.
      let diocesisIds: string[] | null = null;
      if (filtros?.provinciaEclesiasticaId) {
        const { data: diocesisData, error: dErr } = await supabase
          .from('diocesis')
          .select('id')
          .eq('provincia_eclesiastica_id', filtros.provinciaEclesiasticaId);
        if (dErr) throw dErr;
        diocesisIds = (diocesisData || []).map(d => d.id);
        if (diocesisIds.length === 0) return [];
      }

      let query = supabase
        .from('actividades_economicas')
        .select(`
          *,
          diocesis:diocesis_id(id, nombre, tipo)
        `)
        .not('diocesis_id', 'is', null)
        .neq('estado', 'archivada')
        .order('fecha_inicio', { ascending: false });

      if (diocesisIds !== null) {
        query = query.in('diocesis_id', diocesisIds);
      }

      const { data: actividades, error } = await query;
      if (error) throw error;

      const diocesisMap = new Map<string, DiocesisJADGroup>();

      for (const act of (actividades || [])) {
        const diocesisRaw = act.diocesis as { id: string; nombre: string; tipo: string } | null;
        const diocesisId = act.diocesis_id as string;
        const diocesisNombre = diocesisRaw?.nombre || 'Sin Diócesis';
        const diocesisTipo = (diocesisRaw?.tipo as 'diocesis' | 'prefectura') || 'diocesis';

        if (!diocesisMap.has(diocesisId)) {
          diocesisMap.set(diocesisId, {
            diocesisId,
            diocesisNombre,
            diocesisTipo,
            totalActividades: 0,
            totalIngresos: 0,
            totalCostos: 0,
            utilidadNeta: 0,
            actividades: [],
          });
        }

        const grupo = diocesisMap.get(diocesisId)!;
        const ingresos = Number(act.total_ingresos || 0);
        const costos = Number(act.total_costos || 0);

        grupo.actividades.push(act as unknown as ActividadEconomica);
        grupo.totalActividades += 1;
        grupo.totalIngresos += ingresos;
        grupo.totalCostos += costos;
        grupo.utilidadNeta += ingresos - costos;
      }

      return Array.from(diocesisMap.values());
    } catch (error) {
      console.error('Error al obtener actividades JAD agrupadas:', error);
      throw error;
    }
  }

  async getActividadesJANGrouped(): Promise<ProvinciaJANGroup[]> {
    try {
      const { data: actividades, error } = await supabase
        .from('actividades_economicas')
        .select(`
          *,
          provincia_eclesiastica:provincia_eclesiastica_id(id, nombre)
        `)
        .not('provincia_eclesiastica_id', 'is', null)
        .neq('estado', 'archivada')
        .order('fecha_inicio', { ascending: false });

      if (error) throw error;

      const provinciasMap = new Map<string, ProvinciaJANGroup>();

      for (const act of (actividades || [])) {
        const provinciaRaw = act.provincia_eclesiastica as { id: string; nombre: string } | null;
        const provinciaId = act.provincia_eclesiastica_id as string;
        const provinciaNombre = provinciaRaw?.nombre || 'Sin Provincia';

        if (!provinciasMap.has(provinciaId)) {
          provinciasMap.set(provinciaId, {
            provinciaId,
            provinciaNombre,
            totalActividades: 0,
            totalIngresos: 0,
            totalCostos: 0,
            utilidadNeta: 0,
            actividades: [],
          });
        }

        const grupo = provinciasMap.get(provinciaId)!;
        const ingresos = Number(act.total_ingresos || 0);
        const costos = Number(act.total_costos || 0);

        grupo.actividades.push(act as unknown as ActividadEconomica);
        grupo.totalActividades += 1;
        grupo.totalIngresos += ingresos;
        grupo.totalCostos += costos;
        grupo.utilidadNeta += ingresos - costos;
      }

      return Array.from(provinciasMap.values());
    } catch (error) {
      console.error('Error al obtener actividades JAN agrupadas:', error);
      throw error;
    }
  }
}

export const actividadAdminService = new ActividadAdminService();
